import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import { convertKeysToCamelCase } from '@ltvco/refresh-lib/utils';
import {
  useServiceToken,
  useSnackbar,
  type preforeclosureTypes,
  type propertiesTypes,
} from '@ltvco/refresh-lib/v1';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useCallback, useMemo } from 'react';

import { buildPropertyProfessionalInsightsData } from 'api/propertyProfessionalInsights/buildPropertyProfessionalInsightsData';
import { createPropertyProfessionalInsights } from 'api/propertyProfessionalInsights/propertyProfessionalInsights';

import type {
  CreatePropertyProfessionalInsightsParams,
  CreatePropertyProfessionalInsightsResponse,
  PropertyProfessionalInsightsData,
} from 'api/propertyProfessionalInsights/interfaces';

interface TokenData {
  token: string;
}

interface ResponseError extends Error {
  response?: { status?: number };
}

interface LLMPropertyProfessionalInsightsProps {
  property: propertiesTypes.Property | null | undefined;
  permalink?: string | null;
  preforeclosures?: preforeclosureTypes.Preforeclosures;
  reportUpdatedAt?: string | null;
  enabled?: boolean;
}

interface LLMPropertyProfessionalInsightsReturn {
  insights: PropertyProfessionalInsightsData | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

const STORAGE_NS = 'propertyProfessionalInsights';
const MAX_RETRIES = 2;
const isBrowser = typeof window !== 'undefined';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isValidSection = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.description === 'string' &&
  isStringArray(value.bullets);

const isValidInsights = (
  data: unknown
): data is PropertyProfessionalInsightsData => {
  if (!isRecord(data)) {
    return false;
  }

  return (
    typeof data.headerDescription === 'string' &&
    isStringArray(data.tags) &&
    isRecord(data.sections) &&
    isValidSection(data.sections.ownerSituation) &&
    isValidSection(data.sections.valueSnapshot) &&
    isValidSection(data.sections.improvementHistory) &&
    isValidSection(data.sections.liensAndFilings)
  );
};

const storageKeyFor = (permalink: string, revision: string) =>
  `${STORAGE_NS}:${permalink}:${revision}`;

const hashString = (input: string): string => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
};

const requestRevision = (
  params: CreatePropertyProfessionalInsightsParams | null,
  reportUpdatedAt?: string | null
): string =>
  hashString(
    JSON.stringify({
      params: params ?? {},
      reportUpdatedAt: reportUpdatedAt ?? null,
    })
  );

const readCachedInsights = (
  permalink: string,
  revision: string
): PropertyProfessionalInsightsData | undefined => {
  if (!isBrowser || !permalink) {
    return undefined;
  }
  try {
    const raw = window.localStorage.getItem(storageKeyFor(permalink, revision));
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    return isValidInsights(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const writeCachedInsights = (
  permalink: string,
  revision: string,
  value: PropertyProfessionalInsightsData,
  logError: (message: string, error: Error) => void
): void => {
  if (!isBrowser || !permalink) {
    return;
  }
  try {
    window.localStorage.setItem(
      storageKeyFor(permalink, revision),
      JSON.stringify(value)
    );
  } catch (error) {
    logError(
      'property-professional-insights: cache write failed',
      error instanceof Error ? error : new Error(String(error))
    );
  }
};

const toInsightsData = (
  response: CreatePropertyProfessionalInsightsResponse
): unknown => convertKeysToCamelCase(response);

/**
 * Fetches Insights for Professionals from the LLM service for a property report.
 */
export const useLLMPropertyProfessionalInsights = ({
  property,
  permalink,
  preforeclosures,
  reportUpdatedAt,
  enabled = true,
}: LLMPropertyProfessionalInsightsProps): LLMPropertyProfessionalInsightsReturn => {
  const { data: tokenData, isLoading: isTokenLoading } = useServiceToken(
    'llm'
  ) as {
    data: TokenData | undefined;
    isLoading: boolean;
  };
  const { enqueueSnackbar } = useSnackbar();
  const { logError } = useAppConfigContext();

  const permalinkStr = typeof permalink === 'string' ? permalink : '';
  const requestParams = useMemo(
    () =>
      property
        ? buildPropertyProfessionalInsightsData(property, { preforeclosures })
        : null,
    [property, preforeclosures]
  );
  const revision = useMemo(
    () => requestRevision(requestParams, reportUpdatedAt),
    [requestParams, reportUpdatedAt]
  );
  const cachedInsights = useMemo(
    () =>
      permalinkStr ? readCachedInsights(permalinkStr, revision) : undefined,
    [permalinkStr, revision]
  );

  const handleInsightError = useCallback(
    (error: ResponseError) => {
      if (error.response?.status === 429) {
        logError('property-professional-insights: rate limit error', error);
      } else {
        logError('property-professional-insights: generation failed', error);
      }

      enqueueSnackbar('Professional Insights generation failed', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    },
    [enqueueSnackbar, logError]
  );

  const fetchProfessionalInsights = useCallback(async () => {
    if (!property) {
      throw new Error('No property provided');
    }
    if (!tokenData?.token) {
      throw new Error('No authentication token available');
    }
    if (!requestParams) {
      throw new Error('No professional insights request params available');
    }

    const response = await createPropertyProfessionalInsights(
      tokenData.token,
      requestParams
    );

    const insights = toInsightsData(response);
    if (!isValidInsights(insights)) {
      throw new Error('Invalid professional insights response structure');
    }

    if (permalinkStr) {
      writeCachedInsights(permalinkStr, revision, insights, logError);
    }

    return insights;
  }, [
    logError,
    permalinkStr,
    property,
    requestParams,
    revision,
    tokenData?.token,
  ]);

  const query = useQuery({
    queryKey: ['propertyProfessionalInsights', permalinkStr, revision],
    queryFn: fetchProfessionalInsights,
    initialData: cachedInsights,
    retry: (failureCount) => failureCount < MAX_RETRIES,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000), // Exponential backoff with 30s max
    enabled:
      Boolean(enabled) &&
      Boolean(permalinkStr) &&
      Boolean(tokenData?.token) &&
      Boolean(property) &&
      !cachedInsights,
    onError: handleInsightError,
  });

  let insights: PropertyProfessionalInsightsData | null = null;
  if (query.data && isValidInsights(query.data)) {
    insights = query.data;
  } else if (cachedInsights && isValidInsights(cachedInsights)) {
    insights = cachedInsights;
  }

  return {
    insights,
    isLoading:
      !insights && Boolean(enabled) && (isTokenLoading || query.isLoading),
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
